import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { geofencesApi } from '../services/api';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ShieldExclamationIcon,
  ShieldCheckIcon,
  BellAlertIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { SkeletonRow } from '../components/common/Skeleton';
import EmptyState from '../components/common/EmptyState';
import ConfirmModal from '../components/common/ConfirmModal';
import GeofenceDrawDialog, {
  type DrawnGeofence,
} from '../components/geofences/GeofenceDrawDialog';

// Minimum-viable create/edit form. Polygon-draw on map is the natural
// Phase 8: form now supports BOTH circle (centerLat+lng+radius) and
// polygon (GeoJSON geometry) shapes. `geometry` is the canonical when
// present; otherwise the centerLat/lng/radius triplet is used. The
// new GeofenceDrawDialog populates whichever pair matches the user's
// drawing mode.
interface GeofenceFormState {
  id?: string;
  name: string;
  description: string;
  type: 'EXCLUSION' | 'INCLUSION' | 'ALERT';
  // Polygon geometry, if drawn. When set, supersedes the circle triplet.
  geometry: { type: 'Polygon'; coordinates: number[][][] } | null;
  centerLat: string;
  centerLng: string;
  radiusMeters: string;
  altitudeMin: string;
  altitudeMax: string;
  alertSeverity: 'INFO' | 'WARNING' | 'CRITICAL';
  notifyOnEntry: boolean;
  notifyOnExit: boolean;
  isActive: boolean;
}

const EMPTY_FORM: GeofenceFormState = {
  name: '',
  description: '',
  type: 'ALERT',
  geometry: null,
  centerLat: '',
  centerLng: '',
  radiusMeters: '500',
  altitudeMin: '',
  altitudeMax: '',
  alertSeverity: 'WARNING',
  notifyOnEntry: true,
  notifyOnExit: false,
  isActive: true,
};

type GeofenceType = 'EXCLUSION' | 'INCLUSION' | 'ALERT';

const typeConfig: Record<GeofenceType, { icon: any; color: string; bgColor: string; label: string }> = {
  EXCLUSION: { icon: ShieldExclamationIcon, color: 'text-alert', bgColor: 'bg-alert/10', label: 'No-Fly Zone' },
  INCLUSION: { icon: ShieldCheckIcon, color: 'text-accent', bgColor: 'bg-accent/10', label: 'Authorized Area' },
  ALERT: { icon: BellAlertIcon, color: 'text-yellow-400', bgColor: 'bg-yellow-400/10', label: 'Alert Zone' },
};

const severityColors: Record<string, string> = { INFO: 'text-sky', WARNING: 'text-yellow-400', CRITICAL: 'text-alert' };

export default function GeofencesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<GeofenceType | 'ALL'>('ALL');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorState, setEditorState] = useState<GeofenceFormState>(EMPTY_FORM);
  // Separate state for the draw-on-map dialog. We open it from inside
  // the editor and write its result back into the editor form state.
  const [drawOpen, setDrawOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: geofencesData, isLoading } = useQuery({
    queryKey: ['geofences'],
    queryFn: () => geofencesApi.list({}),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => geofencesApi.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['geofences'] }); },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => geofencesApi.update(id, { isActive }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['geofences'] }); },
  });

  const upsertMutation = useMutation({
    mutationFn: async (form: GeofenceFormState) => {
      // Pick polygon OR circle shape — never both. The API route accepts
      // either; sending both is ambiguous so we deliberately choose.
      const shape = form.geometry
        ? { geometry: form.geometry }
        : {
            centerLat: parseFloat(form.centerLat),
            centerLng: parseFloat(form.centerLng),
            radiusMeters: parseFloat(form.radiusMeters),
          };
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        type: form.type,
        ...shape,
        altitudeMin: form.altitudeMin === '' ? undefined : parseFloat(form.altitudeMin),
        altitudeMax: form.altitudeMax === '' ? undefined : parseFloat(form.altitudeMax),
        alertSeverity: form.alertSeverity,
        notifyOnEntry: form.notifyOnEntry,
        notifyOnExit: form.notifyOnExit,
        isActive: form.isActive,
      };
      if (form.id) return geofencesApi.update(form.id, payload);
      return geofencesApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      setEditorOpen(false);
    },
  });

  const openCreate = () => {
    setEditorState(EMPTY_FORM);
    setEditorOpen(true);
  };

  const openEdit = (g: any) => {
    setEditorState({
      id: g.id,
      name: g.name ?? '',
      description: g.description ?? '',
      type: (g.type as GeofenceType) ?? 'ALERT',
      geometry:
        g.geometry?.type === 'Polygon'
          ? { type: 'Polygon', coordinates: g.geometry.coordinates }
          : null,
      centerLat: g.centerLat != null ? String(g.centerLat) : '',
      centerLng: g.centerLng != null ? String(g.centerLng) : '',
      radiusMeters: g.radiusMeters != null ? String(g.radiusMeters) : '500',
      altitudeMin: g.altitudeMin != null ? String(g.altitudeMin) : '',
      altitudeMax: g.altitudeMax != null ? String(g.altitudeMax) : '',
      alertSeverity: g.alertSeverity ?? 'WARNING',
      notifyOnEntry: !!g.notifyOnEntry,
      notifyOnExit: !!g.notifyOnExit,
      isActive: g.isActive ?? true,
    });
    setEditorOpen(true);
  };

  const geofences = geofencesData?.data || [];
  const filteredGeofences = geofences
    .filter((g: any) => typeFilter === 'ALL' ? true : g.type === typeFilter)
    .filter((g: any) => g.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="min-h-screen bg-navy-900 p-6 grid-overlay">
      <div className="mb-6">
        <h1 className="page-title">Geofences</h1>
        <p className="mt-1 text-sm text-gray-500">Define restricted and authorized airspace areas</p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-3 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input type="text" placeholder="Search geofences..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
          </div>
          <div className="flex gap-1 bg-navy-800 rounded-lg p-1 border border-navy-700">
            <button onClick={() => setTypeFilter('ALL')} className={clsx('px-3 py-1.5 rounded-md text-xs font-medium transition-all', typeFilter === 'ALL' ? 'bg-accent text-navy-900' : 'text-gray-400 hover:text-white')}>All</button>
            {(Object.keys(typeConfig) as GeofenceType[]).map((type) => (
              <button key={type} onClick={() => setTypeFilter(type)} className={clsx('px-3 py-1.5 rounded-md text-xs font-medium transition-all', typeFilter === type ? 'bg-accent text-navy-900' : 'text-gray-400 hover:text-white')}>
                {typeConfig[type].label}
              </button>
            ))}
          </div>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2"><PlusIcon className="h-4 w-4" />New Geofence</button>
      </div>

      {isLoading ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} className="glass-card" />)}</div>
      ) : filteredGeofences.length === 0 ? (
        <EmptyState icon={ShieldExclamationIcon} title="No geofences found" description={searchTerm ? 'Try adjusting your search or filter.' : 'Get started by creating a new geofence.'} />
      ) : (
        <div className="space-y-3">
          {filteredGeofences.map((geofence: any) => {
            const config = typeConfig[geofence.type as GeofenceType];
            const Icon = config.icon;
            return (
              <div key={geofence.id} className="glass-card p-4 glow-border">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={clsx('p-2 rounded-lg', config.bgColor, config.color)}><Icon className="h-5 w-5" /></div>
                      <div>
                        <h3 className="text-base font-semibold text-white">{geofence.name}</h3>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-xs text-gray-500">{config.label}</span>
                          <span className={clsx('text-xs font-medium', severityColors[geofence.alertSeverity as string])}>{geofence.alertSeverity}</span>
                          <span className={clsx('text-[10px] px-2 py-0.5 rounded-full font-semibold', geofence.isActive ? 'bg-accent/10 text-accent' : 'bg-gray-500/10 text-gray-500')}>
                            {geofence.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                    {geofence.description && <p className="text-sm text-gray-400 mb-2 ml-12">{geofence.description}</p>}
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500 ml-12">
                      {geofence.altitudeMin !== null && geofence.altitudeMax !== null && <span>Altitude: {geofence.altitudeMin}m – {geofence.altitudeMax}m</span>}
                      <span>Type: {geofence.geometry?.type || 'Unknown'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button onClick={() => toggleActiveMutation.mutate({ id: geofence.id, isActive: !geofence.isActive })}
                      className={clsx('px-3 py-1.5 text-xs rounded-lg transition-colors font-medium', geofence.isActive ? 'btn-secondary' : 'btn-primary')}>
                      {geofence.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => openEdit(geofence)} className="p-2 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-navy-700"><PencilIcon className="h-4 w-4" /></button>
                    <button onClick={() => setDeleteTarget(geofence.id)} className="p-2 text-gray-500 hover:text-alert transition-colors rounded-lg hover:bg-alert/10"><TrashIcon className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: geofences.length, color: 'text-white' },
          { label: 'Active', value: geofences.filter((g: any) => g.isActive).length, color: 'text-accent' },
          { label: 'No-Fly Zones', value: geofences.filter((g: any) => g.type === 'EXCLUSION').length, color: 'text-alert' },
          { label: 'Alert Zones', value: geofences.filter((g: any) => g.type === 'ALERT').length, color: 'text-yellow-400' },
        ].map((s) => (
          <div key={s.label} className="glass-card p-4">
            <p className="text-xs text-gray-500">{s.label}</p>
            <p className={`text-2xl font-display font-bold ${s.color} mt-1`}>{s.value}</p>
          </div>
        ))}
      </div>

      <ConfirmModal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        onConfirm={() => { if (deleteTarget) deleteMutation.mutate(deleteTarget); }}
        title="Delete Geofence" message="Are you sure you want to delete this geofence? This action cannot be undone."
        confirmLabel="Delete" variant="danger" />

      <GeofenceEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        state={editorState}
        setState={setEditorState}
        onSubmit={() => upsertMutation.mutate(editorState)}
        submitting={upsertMutation.isPending}
        error={(upsertMutation.error as Error | null)?.message ?? null}
        onDraw={() => setDrawOpen(true)}
      />

      <GeofenceDrawDialog
        open={drawOpen}
        onClose={() => setDrawOpen(false)}
        onConfirm={(shape: DrawnGeofence) => {
          // Replace whichever side of the form (polygon vs circle) the user
          // just drew. We clear the other side to keep the payload one-shape.
          if (shape.kind === 'polygon') {
            setEditorState({
              ...editorState,
              geometry: { type: 'Polygon', coordinates: shape.coordinates },
              centerLat: '',
              centerLng: '',
              radiusMeters: '',
            });
          } else {
            setEditorState({
              ...editorState,
              geometry: null,
              centerLat: shape.centerLat.toFixed(6),
              centerLng: shape.centerLng.toFixed(6),
              radiusMeters: Math.round(shape.radiusMeters).toString(),
            });
          }
          setDrawOpen(false);
        }}
      />
    </div>
  );
}

// ----- Editor modal --------------------------------------------------------

interface EditorProps {
  open: boolean;
  onClose: () => void;
  state: GeofenceFormState;
  setState: (s: GeofenceFormState) => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
  onDraw: () => void;
}

function GeofenceEditorModal({
  open, onClose, state, setState, onSubmit, submitting, error, onDraw,
}: EditorProps) {
  // Reset error reset on close so the next open doesn't show a stale msg.
  useEffect(() => { if (!open) { /* no-op; parent owns state */ } }, [open]);

  const update = <K extends keyof GeofenceFormState>(k: K, v: GeofenceFormState[K]) =>
    setState({ ...state, [k]: v });

  // Polygon satisfies the geometry requirement; circle requires center+radius.
  // Either path requires a name.
  const hasShape =
    state.geometry != null ||
    (state.centerLat !== '' && state.centerLng !== '' && state.radiusMeters !== '');
  const valid = state.name.trim().length > 0 && hasShape;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-navy-900/80 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-lg bg-navy-900 border border-navy-700 rounded-lg shadow-xl">
                <div className="flex items-center justify-between p-5 border-b border-navy-700">
                  <Dialog.Title className="text-base font-semibold text-white">
                    {state.id ? 'Edit Geofence' : 'New Geofence'}
                  </Dialog.Title>
                  <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white hover:bg-navy-800 rounded-md">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                  <Field label="Name *">
                    <input type="text" value={state.name} onChange={(e) => update('name', e.target.value)}
                      className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
                  </Field>
                  <Field label="Description">
                    <textarea value={state.description} onChange={(e) => update('description', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Type">
                      <select value={state.type} onChange={(e) => update('type', e.target.value as GeofenceType)}
                        className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent">
                        <option value="ALERT">Alert Zone</option>
                        <option value="EXCLUSION">No-Fly Zone</option>
                        <option value="INCLUSION">Authorized Area</option>
                      </select>
                    </Field>
                    <Field label="Severity">
                      <select value={state.alertSeverity} onChange={(e) => update('alertSeverity', e.target.value as 'INFO' | 'WARNING' | 'CRITICAL')}
                        className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent">
                        <option value="INFO">INFO</option>
                        <option value="WARNING">WARNING</option>
                        <option value="CRITICAL">CRITICAL (critical infrastructure)</option>
                      </select>
                    </Field>
                  </div>
                  {/* Draw-on-map shortcut. Visually-distinct from the typed
                      coordinate fields below so the operator knows this is
                      the preferred path (typed coords are an escape hatch). */}
                  <div className="flex items-center justify-between gap-3 p-3 bg-navy-800/60 border border-navy-700 rounded-lg">
                    <div className="text-xs">
                      {state.geometry ? (
                        <span className="text-accent font-medium">
                          Polygon drawn ({state.geometry.coordinates[0]?.length ?? 0} vertices)
                        </span>
                      ) : state.centerLat && state.centerLng && state.radiusMeters ? (
                        <span className="text-accent font-medium">
                          Circle drawn ({state.centerLat}, {state.centerLng}) · {state.radiusMeters} m
                        </span>
                      ) : (
                        <span className="text-gray-400">
                          Use the map drawing tool or enter coordinates manually.
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={onDraw}
                      className="btn-secondary flex items-center gap-2 text-xs"
                    >
                      <MapPinIcon className="h-4 w-4" />
                      Draw on map
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Center Latitude">
                      <input type="number" step="0.00001" value={state.centerLat}
                        onChange={(e) => update('centerLat', e.target.value)}
                        disabled={state.geometry != null}
                        className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent disabled:opacity-50" />
                    </Field>
                    <Field label="Center Longitude">
                      <input type="number" step="0.00001" value={state.centerLng}
                        onChange={(e) => update('centerLng', e.target.value)}
                        disabled={state.geometry != null}
                        className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent disabled:opacity-50" />
                    </Field>
                    <Field label="Radius (m)">
                      <input type="number" min="1" value={state.radiusMeters}
                        onChange={(e) => update('radiusMeters', e.target.value)}
                        disabled={state.geometry != null}
                        className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent disabled:opacity-50" />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Altitude Min (m MSL)">
                      <input type="number" value={state.altitudeMin} onChange={(e) => update('altitudeMin', e.target.value)}
                        className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" placeholder="any" />
                    </Field>
                    <Field label="Altitude Max (m MSL)">
                      <input type="number" value={state.altitudeMax} onChange={(e) => update('altitudeMax', e.target.value)}
                        className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" placeholder="any" />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-white">
                      <input type="checkbox" checked={state.notifyOnEntry} onChange={(e) => update('notifyOnEntry', e.target.checked)}
                        className="h-4 w-4 rounded border-navy-600 bg-navy-800 text-accent" />
                      Alert on entry (creates GEOFENCE_BREACH alert)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-white">
                      <input type="checkbox" checked={state.notifyOnExit} onChange={(e) => update('notifyOnExit', e.target.checked)}
                        className="h-4 w-4 rounded border-navy-600 bg-navy-800 text-accent" />
                      Alert on exit
                    </label>
                    <label className="flex items-center gap-2 text-sm text-white">
                      <input type="checkbox" checked={state.isActive} onChange={(e) => update('isActive', e.target.checked)}
                        className="h-4 w-4 rounded border-navy-600 bg-navy-800 text-accent" />
                      Active
                    </label>
                  </div>
                  {error && <div className="text-sm text-rose-400">{error}</div>}
                </div>
                <div className="flex items-center justify-end gap-2 p-4 border-t border-navy-700">
                  <button onClick={onClose} className="btn-secondary">Cancel</button>
                  <button onClick={onSubmit} disabled={!valid || submitting} className="btn-primary disabled:opacity-50">
                    {submitting ? 'Saving…' : state.id ? 'Save changes' : 'Create geofence'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1 block">
        {label}
      </span>
      {children}
    </label>
  );
}
